/**
 * V2ScreenFlowCoordinator - Native V2 coordinator for StepNode entities on flow canvas
 *
 * Native V2 coordinator with V2-owned dependencies:
 * - FlowOnboardingScreen → FlowStepNode
 * - StepNodeScreenService for Firebase `screensV2` persistence
 * - onboardingScreenAdapter → v2ScreenAdapter
 * - onboardingScreenTransitionManager → v2TransitionManager
 * - onboardingScreenRegistry → v2ScreenRegistry
 * - V2-native connection-handle extraction through V2ActionHandleResolver
 *
 * NO V1 types internally, NO bridge conversion. Canvas operates on V2 StepNode natively.
 *
 * Sprint: schema-v2-migration, Sprint 2
 * Related: OnboardingScreenFlowCoordinator (V1), OnboardingScreenFlowCoordinatorV2 (Sprint 1 bridge)
 */

import type { StepNode } from '@segmently/schema-models/v2';
import type { ReactFlowCanvasHandle, EntityMove, PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';
import type { FlowCoordinator, CreateEntityResult, CreateTransitionInput, LoadingCallbacks, FlowAnimationDefaults } from '../shared/FlowCoordinator';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import { v2ScreenRegistry } from './V2ScreenRegistry';
import { v2ScreenAdapter, type FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { v2TransitionManager } from './V2TransitionManager';
import { clearAllStepEdges } from '@modules/reactFlow/utils/transformers/V2EdgeCleaner';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import { buildV2ActionHandles } from '@modules/reactFlow/services/shared/V2ActionHandleResolver';
import { StepNodeScreenService } from '@modules/firebase-core/services/StepNodeScreenService';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';

interface EdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  label: string;
  type: 'transition';
  animated: boolean;
  data: {
    sourceEntityId: string;
    targetEntityId: string;
    sourceActionKey: string;
    rules?: unknown[];
    /** Per-edge visual transition override (Route.transition) */
    transition?: unknown;
    onEdgeClick?: (edgeId: string, edgeData: any) => void;
  };
}

/**
 * V2ScreenFlowCoordinator - Concrete implementation for V2 StepNode screens
 *
 * Implements: FlowCoordinator<FlowStepNode, StepNodeScreenService>
 */
export class V2ScreenFlowCoordinator implements FlowCoordinator<FlowStepNode, StepNodeScreenService> {
  private initialized: boolean = false;
  private screenService: StepNodeScreenService | null = null;
  private onboardingService: OnboardingService | null = null;
  private projectId: string;
  private onboardingId: string;
  private versionId: string;
  private canvasHandle: ReactFlowCanvasHandle | null = null;
  private loadingCallbacks?: LoadingCallbacks;
  private launchScreenId: string | null = null;
  private onLaunchScreenChanged?: (newLaunchScreenId: string) => void;
  // Version-level animation defaults (screensGraph.*) — read together with
  // launchScreenId (same getVersion call). The editor preview needs
  // defaultScreenAnimation to show theme-inherited entrances.
  private defaultScreenAnimation: Record<string, unknown> | null = null;
  private defaultTransition: Record<string, unknown> | null = null;

  /**
   * Document-level metadata from version.screensGraph (e.g. kind: 'article').
   * Loaded together with launchScreenId; the canvas editor threads it into the
   * assembled FlowDocument so article-only editor features can gate on it.
   */
  public screensGraphMetadata: Record<string, unknown> | null = null;

  // Template source for template lookup (set by canvas editor)
  // V2 panel returns native StepNode templates only.
  private templateSource: ((templateId: string) => StepNode | null) | null = null;

  public targetLocales: string[] = [];
  public defaultLanguage: string = 'en-US';

  constructor(projectId: string, onboardingId: string, versionId: string) {
    this.projectId = projectId;
    this.onboardingId = onboardingId;
    this.versionId = versionId;
    this.screenService = new StepNodeScreenService();
    this.onboardingService = new OnboardingService();
    this.initialized = true;
    console.log('[V2Coordinator] Initialized with:', { projectId, onboardingId, versionId });
  }

  // ============================================================================
  // GETTER METHODS
  // ============================================================================

  public getProjectId(): string {
    return this.projectId;
  }

  public getOnboardingId(): string {
    return this.onboardingId;
  }

  public getVersionId(): string {
    return this.versionId;
  }

  public getScreenService(): StepNodeScreenService | null {
    return this.screenService;
  }

  /**
   * Set template source for template lookup during createEntity.
   * Templates are native V2 StepNodes.
   */
  public setTemplateSource(getter: (templateId: string) => StepNode | null): void {
    this.templateSource = getter;
    console.log('[V2Coordinator] Template source set');
  }

  /**
   * Set target locales and default language for editor/runtime context.
   * Template creation is V2-native and does not run V1 locale transforms.
   */
  public setLocaleSettings(targetLocales: string[], defaultLanguage: string): void {
    this.targetLocales = targetLocales;
    this.defaultLanguage = defaultLanguage;
    console.log('[V2Coordinator] Locale settings set:', { targetLocales, defaultLanguage });
  }

  /**
   * Register callback for launch screen changes.
   */
  public setOnLaunchScreenChanged(callback: (newLaunchScreenId: string) => void): void {
    this.onLaunchScreenChanged = callback;
  }

  // ============================================================================
  // INITIALIZATION & LIFECYCLE
  // ============================================================================

  /**
   * Load V2 screens from Firebase screensV2 subcollection.
   * Reads StepNode[] directly — no V1 conversion.
   */
  async loadScreensFromFirebase(): Promise<void> {
    if (!this.screenService) {
      console.warn('[V2Coordinator] Cannot load screens - service not initialized');
      return;
    }

    console.log('[V2Coordinator] Loading V2 screens from Firebase...');
    console.log(`  Path: projects/${this.projectId}/onboardings/${this.onboardingId}/versions/${this.versionId}/screensV2`);

    const steps = await this.screenService.listScreens(
      this.projectId,
      this.onboardingId,
      this.versionId
    );

    console.log(`[V2Coordinator] Found ${steps.length} V2 screens`);

    // Convert StepNode → FlowStepNode (adds entityId) and register
    steps.forEach((step: StepNode) => {
      const flowStep = v2ScreenAdapter.toFlowEntity(step);
      v2ScreenRegistry.register(flowStep);
    });

    await this.loadLaunchScreenId();

    console.log(`[V2Coordinator] Loaded ${steps.length} screens, launchScreenId: ${this.launchScreenId}`);
  }

  private async loadLaunchScreenId(): Promise<void> {
    if (!this.onboardingService) return;

    try {
      const version = await this.onboardingService.getVersion(
        this.projectId,
        this.onboardingId,
        this.versionId
      );

      this.launchScreenId = version?.screensGraph?.launchScreenId || null;
      const graph = version?.screensGraph as Record<string, unknown> | undefined;
      this.defaultScreenAnimation = (graph?.defaultScreenAnimation as Record<string, unknown> | undefined) ?? null;
      this.defaultTransition = (graph?.defaultTransition as Record<string, unknown> | undefined) ?? null;
      this.screensGraphMetadata = (graph?.metadata as Record<string, unknown> | undefined) ?? null;
    } catch (error) {
      console.error('[V2Coordinator] Failed to load launchScreenId:', error);
    }
  }

  /** Version-level animation defaults captured with the last graph-meta load (optional FlowCoordinator capability). */
  public getAnimationDefaults(): FlowAnimationDefaults {
    return {
      defaultScreenAnimation: this.defaultScreenAnimation,
      defaultTransition: this.defaultTransition,
    };
  }

  /** Re-read screensGraph animation defaults (after Apply→Animations writes them). */
  public async reloadAnimationDefaults(): Promise<void> {
    await this.loadLaunchScreenId();
  }

  setCanvasHandle(handle: ReactFlowCanvasHandle | null): void {
    this.canvasHandle = handle;

    if (handle && this.initialized) {
      this.loadExistingScreensToCanvas();
    }
  }

  setLoadingCallbacks(callbacks: LoadingCallbacks): void {
    this.loadingCallbacks = callbacks;
  }

  private async wrapWithLoading<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    try {
      this.loadingCallbacks?.onSyncStart?.(operation);
      const result = await fn();
      this.loadingCallbacks?.onSyncEnd?.();
      return result;
    } catch (error) {
      this.loadingCallbacks?.onSyncError?.(error as Error, operation);
      this.loadingCallbacks?.onSyncEnd?.();
      throw error;
    }
  }

  async getLaunchEntityId(): Promise<string | null> {
    if (this.launchScreenId) {
      return this.launchScreenId;
    }
    await this.loadLaunchScreenId();
    return this.launchScreenId;
  }

  /**
   * Set launch screen.
   * Updates version.screensGraph.launchScreenId in Firebase.
   * Deletes incoming edges to launch screen (launch screen has no incoming transitions).
   */
  async setLaunchEntity(entityId: string): Promise<{
    deletedTransitionIds: string[];
    affectedEntitiesBackup: Map<string, FlowStepNode>;
  }> {
    return this.wrapWithLoading('Setting launch screen', async () => {
      console.log('[V2Coordinator] Setting launch screen:', entityId);

      const previousLaunchId = this.launchScreenId;

      const step = v2ScreenRegistry.get(entityId);
      if (!step) {
        throw new Error(`Screen not found: ${entityId}`);
      }

      // Find and delete incoming edges
      const allTransitions = this.computeAllTransitions();
      const incomingTransitions = allTransitions.filter(t => t.targetEntityId === entityId);
      const deletedTransitionIds = incomingTransitions.map(t => t.id);

      // Backup affected source screens
      const affectedEntitiesBackup = new Map<string, FlowStepNode>();
      for (const transition of incomingTransitions) {
        const sourceStep = v2ScreenRegistry.get(transition.sourceEntityId);
        if (sourceStep) {
          affectedEntitiesBackup.set(
            transition.sourceEntityId,
            JSON.parse(JSON.stringify(sourceStep))
          );
        }
      }

      if (deletedTransitionIds.length > 0) {
        await this.deleteTransitions(deletedTransitionIds);
        console.log(`[V2Coordinator] Deleted ${deletedTransitionIds.length} incoming edges`);
      }

      // Update version.screensGraph.launchScreenId
      if (this.onboardingService) {
        await this.onboardingService.updateVersion(
          this.projectId,
          this.onboardingId,
          this.versionId,
          { screensGraph: { launchScreenId: entityId } as any }
        );
      }

      this.launchScreenId = entityId;
      this.onLaunchScreenChanged?.(entityId);

      // Update Canvas indicators
      if (this.canvasHandle) {
        if (previousLaunchId && previousLaunchId !== entityId) {
          this.canvasHandle.updateNodeData(previousLaunchId, { isLaunchScreen: false });
        }
        this.canvasHandle.updateNodeData(entityId, { isLaunchScreen: true });
      }

      console.log('[V2Coordinator] Launch screen set:', entityId);
      return { deletedTransitionIds, affectedEntitiesBackup };
    });
  }

  // ============================================================================
  // FLOWCOORDINATOR INTERFACE IMPLEMENTATION
  // ============================================================================

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async initialize(_projectId?: string, _onboardingId?: string, _versionId?: string, launchScreenId?: string): Promise<void> {
    if (launchScreenId) {
      this.launchScreenId = launchScreenId;
    }
    console.log('[V2Coordinator] initialize() called');
  }

  getAllEntities(): FlowStepNode[] {
    return v2ScreenRegistry.getAll();
  }

  /**
   * Generate a unique screen name based on existing names in the registry.
   * Pattern: "Hero Content" → "Hero Content 2" → "Hero Content 3" → ...
   */
  private generateUniqueName(baseName: string): string {
    const allEntities = v2ScreenRegistry.getAll();
    const usedNames = new Set(allEntities.map(e => e.name || ''));

    if (!usedNames.has(baseName)) return baseName;

    let counter = 2;
    while (usedNames.has(`${baseName} ${counter}`)) {
      counter++;
    }
    return `${baseName} ${counter}`;
  }

  /**
   * Create new V2 screen from a native V2 StepNode template.
   *
   * Generates a unique screen name to avoid duplicates on canvas.
   */
  async createEntity(data: Partial<FlowStepNode>, position: { x: number; y: number }): Promise<CreateEntityResult<FlowStepNode>> {
    const isFirstScreen = v2ScreenRegistry.getAll().length === 0;

    const templateId = (data as any).templateId || (data as any).id;

    if (templateId && this.templateSource) {
      const template = this.templateSource(templateId);

      if (template) {
        console.log('[V2Coordinator] Creating V2 screen from template:', {
          templateId,
          screenType: (template as any).screenType,
          name: (template as any).name
        });

        // ── FlexibleLayout stub ──────────────────────────────────────────────
        // ScreenTitleSubtitlePicker is used as the "create FlexibleLayout" trigger
        // in the template panel (temporary approach until a dedicated template exists).
        if ((template as any).screenType === 'ScreenTitleSubtitlePicker') {
          const flexId = crypto.randomUUID();
          const flexStep: FlowStepNode = {
            id: flexId,
            entityId: flexId,
            position,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            screenType: 'FlexibleLayout' as any,
            name: this.generateUniqueName('Flexible Layout'),
            tags: [],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            content: {
              flexibleLayout: { scrollable: true, sections: [] },
              canvas: {
                background: { kind: 'solid', content: { color: '#ffffff' } },
              },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          };

          await this.wrapWithLoading('Creating screen', async () => {
            const firebaseStep = v2ScreenAdapter.toFirebaseStep(flexStep);
            await this.screenService!.addScreen(
              this.projectId, this.onboardingId, this.versionId, firebaseStep
            );
          });

          this.addStepToCanvas(flexStep);

          if (isFirstScreen) {
            console.log('[V2Coordinator] Auto-assigning FlexibleLayout as launch:', flexId);
            await this.setLaunchEntity(flexId);
          }

          return { entity: flexStep };
        }
        // ─────────────────────────────────────────────────────────────────────

        const newStep = v2ScreenAdapter.createFromV2Template(template, position);

        // Generate unique name to avoid duplicates on canvas
        newStep.name = this.generateUniqueName(newStep.name || 'Screen');

        await this.wrapWithLoading('Creating screen', async () => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(newStep);
          await this.screenService!.addScreen(
            this.projectId,
            this.onboardingId,
            this.versionId,
            firebaseStep
          );
        });

        this.addStepToCanvas(newStep);

        if (isFirstScreen) {
          console.log('[V2Coordinator] Auto-assigning first screen as launch:', newStep.entityId);
          await this.setLaunchEntity(newStep.entityId);
        }

        return { entity: newStep };
      }

      console.warn('[V2Coordinator] Template not found:', templateId);
    }

    // Fallback: allow callers to pass a native V2 StepNode directly.
    if (!('content' in data) || typeof data.screenType !== 'string') {
      throw new Error('V2 screen creation requires a native V2 StepNode template');
    }

    console.log('[V2Coordinator] Creating screen from native V2 data');
    const newStep = v2ScreenAdapter.createFromV2Template(data as StepNode, position);

    await this.wrapWithLoading('Creating screen', async () => {
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(newStep);
      await this.screenService!.addScreen(
        this.projectId,
        this.onboardingId,
        this.versionId,
        firebaseStep
      );
    });

    this.addStepToCanvas(newStep);

    if (isFirstScreen) {
      console.log('[V2Coordinator] Auto-assigning first screen as launch:', newStep.entityId);
      await this.setLaunchEntity(newStep.entityId);
    }

    return { entity: newStep };
  }

  updateEntity(entityId: string, updates: Partial<FlowStepNode>): void {
    const current = v2ScreenRegistry.get(entityId);
    if (!current) {
      console.warn(`[V2Coordinator] Cannot update non-existent screen: ${entityId}`);
      return;
    }

    const updated = { ...current, ...updates };
    v2ScreenRegistry.update(entityId, updated);
  }

  async restoreEntities(
    steps: FlowStepNode[],
    affectedStepsBackup?: Map<string, FlowStepNode>
  ): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);
      await this.wrapWithLoading('Restoring screen', async () => {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
        await this.screenService!.addScreen(
          this.projectId,
          this.onboardingId,
          this.versionId,
          firebaseStep
        );
      });
    }

    // Restore connections in affected screens
    if (affectedStepsBackup && affectedStepsBackup.size > 0) {
      for (const [stepId, originalStep] of affectedStepsBackup) {
        v2ScreenRegistry.update(stepId, originalStep);

        await this.wrapWithLoading('Restoring connections', async () => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(originalStep);
          await this.screenService!.replaceScreen(
            this.projectId,
            this.onboardingId,
            this.versionId,
            stepId,
            firebaseStep
          );
        });
      }
    }

    const restoredTransitions = this.computeTransitionsFromSteps(steps);
    this.syncEdgesToCanvas(restoredTransitions);
  }

  async deleteEntities(stepIds: string[]): Promise<{
    deletedTransitionIds: string[];
    affectedScreensBackup: Map<string, FlowStepNode>;
  }> {
    const allTransitions = this.computeAllTransitions();
    const deletedTransitionIds = allTransitions
      .filter(t => stepIds.includes(t.sourceEntityId) || stepIds.includes(t.targetEntityId))
      .map(t => t.id);

    // Cleanup incoming connections from non-deleted screens
    const affectedScreensBackup = await this.cleanupIncomingConnections(stepIds);

    // Delete from registry
    stepIds.forEach(id => {
      v2ScreenRegistry.delete(id);
    });

    // Update Canvas
    this.executeCanvasOperation(handle => handle.removeNodes(stepIds));
    this.syncEdgesToCanvas();

    // Delete from Firebase
    await this.wrapWithLoading(`Deleting ${stepIds.length} screen(s)`, async () => {
      await Promise.all(stepIds.map(id =>
        this.screenService!.deleteScreen(this.projectId, this.onboardingId, this.versionId, id)
      ));
    });

    return { deletedTransitionIds, affectedScreensBackup };
  }

  async updateEntityPositions(moves: EntityMove[]): Promise<void> {
    const normalizedMoves = moves.map(move => ({
      entityId: move.entityId,
      position: {
        x: move.newPosition.x ?? 0,
        y: move.newPosition.y ?? 0
      }
    }));

    // Update Registry
    normalizedMoves.forEach(({ entityId, position }) => {
      v2ScreenRegistry.updatePosition(entityId, position);
    });

    // Update Firebase (merge update for position-only change is fine)
    if (this.screenService) {
      await this.wrapWithLoading(`Moving ${moves.length} screen(s)`, async () => {
        await Promise.all(normalizedMoves.map(({ entityId, position }) =>
          this.screenService!.updateScreen(
            this.projectId,
            this.onboardingId,
            this.versionId,
            entityId,
            { position } as any
          )
        ));
      });
    }

    // Update Canvas
    this.executeCanvasOperation(handle =>
      handle.updateNodePositions(moves.map(m => ({
        entityId: m.entityId,
        oldPosition: m.oldPosition,
        newPosition: m.newPosition
      })))
    );
  }

  /**
   * Clone V2 screen with new UUID and cleared edges.
   *
   * Uses clearAllStepEdges() for comprehensive V2 edge clearing
   * (replaces V1's manual struct-level clearing).
   */
  async cloneEntity(sourceEntityId: string, offset: PositionOnConfigurator): Promise<CreateEntityResult<FlowStepNode>> {
    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) {
      throw new Error(`Screen not found: ${sourceEntityId}`);
    }

    const newPosition = {
      x: (sourceStep.position?.x || 0) + (offset.x ?? 0),
      y: (sourceStep.position?.y || 0) + (offset.y ?? 0)
    };

    // Deep copy and clear all V2 edges
    const cloned = JSON.parse(JSON.stringify(sourceStep)) as FlowStepNode;
    const withoutEdges = clearAllStepEdges(cloned as StepNode);
    const uuid = crypto.randomUUID();

    const clonedStep: FlowStepNode = {
      ...(withoutEdges as FlowStepNode),
      id: uuid,
      entityId: uuid,
      position: newPosition,
      name: `${sourceStep.name || 'Screen'} (Copy)`,
    };

    // Save to Firebase
    await this.wrapWithLoading('Cloning screen', async () => {
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(clonedStep);
      await this.screenService!.addScreen(
        this.projectId,
        this.onboardingId,
        this.versionId,
        firebaseStep
      );
    });

    this.addStepToCanvas(clonedStep);

    return { entity: clonedStep };
  }

  /**
   * Add pre-prepared V2 screens to canvas and Firebase.
   * Used by PasteEntitiesCommand for paste operations.
   *
   * Screens must already have final IDs, positions, edges, and locales.
   */
  async addPreparedScreens(steps: FlowStepNode[]): Promise<void> {
    for (const step of steps) {
      this.addStepToCanvas(step);

      await this.wrapWithLoading('Pasting screen', async () => {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
        await this.screenService!.addScreen(
          this.projectId,
          this.onboardingId,
          this.versionId,
          firebaseStep
        );
      });
    }

    // Sync all edges after all screens added (inter-screen edges become visible)
    this.syncEdgesToCanvas();
  }

  /**
   * Update screen content and refresh canvas connections.
   *
   * Uses replaceScreen() (full overwrite, no merge) to avoid Firestore's
   * deep merge preserving stale nested edge data.
   */
  async updateEntityAndRefreshConnections(entityId: string, updatedStep: FlowStepNode): Promise<void> {
    // Update Registry
    v2ScreenRegistry.update(entityId, updatedStep);

    // Update Firebase (full replace)
    if (this.screenService) {
      await this.wrapWithLoading('Updating screen', async () => {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(updatedStep);
        await this.screenService!.replaceScreen(
          this.projectId,
          this.onboardingId,
          this.versionId,
          entityId,
          firebaseStep
        );
      });
    }

    // Update Canvas node handles (V2-native handle discovery)
    const handles = buildV2ActionHandles(updatedStep);
    this.executeCanvasOperation(handle => handle.updateNodeData(entityId, { handles }));

    // Refresh all transitions
    const allEdges = this.computeAllTransitions();
    this.executeCanvasOperation(handle => handle.syncEdges(allEdges));
  }

  // ============================================================================
  // TRANSITION OPERATIONS
  // ============================================================================

  async createTransition(input: CreateTransitionInput): Promise<EntityTransition> {
    const { sourceEntityId, targetEntityId, sourceActionKey, label } = input;

    const sourceStep = v2ScreenRegistry.get(sourceEntityId);
    if (!sourceStep) {
      throw new Error(`Source screen not found: ${sourceEntityId}`);
    }

    if (!v2ScreenRegistry.has(targetEntityId)) {
      throw new Error(`Target screen not found: ${targetEntityId}`);
    }

    if (sourceEntityId === targetEntityId) {
      throw new Error('Cannot create transition to same screen');
    }

    const transitionId = TransitionIdGenerator.generate(sourceEntityId, sourceActionKey, targetEntityId);
    const transition: EntityTransition = {
      id: transitionId,
      sourceEntityId,
      targetEntityId,
      sourceActionKey,
      label: label || '',
    };

    // Add edge at correct V2 content path via V2TransitionManager
    const updatedStepNode = v2TransitionManager.addTransition(sourceStep as StepNode, transition);
    v2ScreenRegistry.update(sourceEntityId, { ...updatedStepNode, entityId: sourceStep.entityId } as FlowStepNode);

    this.executeCanvasOperation(handle => handle.addEdge(this.createEdgeData(transition)));

    // Persist (full replace to ensure edge structure is correct)
    await this.wrapWithLoading('Creating transition', async () => {
      const registryStep = v2ScreenRegistry.get(sourceEntityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(
        this.projectId,
        this.onboardingId,
        this.versionId,
        sourceEntityId,
        firebaseStep
      );
    });

    return transition;
  }

  async updateTransition(transitionId: string, updates: Partial<Omit<EntityTransition, 'id'>>): Promise<EntityTransition> {
    const found = this.findTransitionOwner(transitionId);
    if (!found) {
      throw new Error(`Transition not found: ${transitionId}`);
    }

    const { step: ownerStep, transition: existingTransition } = found;
    const updatedStepNode = v2TransitionManager.updateTransition(ownerStep as StepNode, transitionId, updates);
    v2ScreenRegistry.update(ownerStep.entityId, { ...updatedStepNode, entityId: ownerStep.entityId } as FlowStepNode);

    const updatedTransition: EntityTransition = { ...existingTransition, ...updates };
    this.executeCanvasOperation(handle => handle.updateEdge(this.createEdgeData(updatedTransition)));

    await this.wrapWithLoading('Updating transition', async () => {
      const registryStep = v2ScreenRegistry.get(ownerStep.entityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(
        this.projectId,
        this.onboardingId,
        this.versionId,
        ownerStep.id!,
        firebaseStep
      );
    });

    return updatedTransition;
  }

  async deleteTransition(transitionId: string): Promise<void> {
    const found = this.findTransitionOwner(transitionId);
    if (!found) {
      throw new Error(`Transition not found: ${transitionId}`);
    }

    const { step: ownerStep } = found;
    const updatedStepNode = v2TransitionManager.removeTransition(ownerStep as StepNode, transitionId);
    v2ScreenRegistry.update(ownerStep.entityId, { ...updatedStepNode, entityId: ownerStep.entityId } as FlowStepNode);

    this.executeCanvasOperation(handle => handle.removeEdge(transitionId));

    await this.wrapWithLoading('Deleting transition', async () => {
      const registryStep = v2ScreenRegistry.get(ownerStep.entityId)!;
      const firebaseStep = v2ScreenAdapter.toFirebaseStep(registryStep);
      await this.screenService!.replaceScreen(
        this.projectId,
        this.onboardingId,
        this.versionId,
        ownerStep.id!,
        firebaseStep
      );
    });
  }

  async deleteTransitions(transitionIds: string[]): Promise<void> {
    const stepUpdates = new Map<string, FlowStepNode>();
    const allSteps = v2ScreenRegistry.getAll();

    for (const transitionId of transitionIds) {
      for (const step of allSteps) {
        const transitions = v2TransitionManager.extractTransitions(step as StepNode);
        if (transitions.some(t => t.id === transitionId)) {
          const currentStep = stepUpdates.get(step.entityId) || step;
          const updatedStepNode = v2TransitionManager.removeTransition(currentStep as StepNode, transitionId);
          stepUpdates.set(step.entityId, { ...updatedStepNode, entityId: step.entityId } as FlowStepNode);
          break;
        }
      }
    }

    // Update registry
    stepUpdates.forEach((step, entityId) => {
      v2ScreenRegistry.update(entityId, step);
    });

    // Update canvas
    this.executeCanvasOperation(handle => handle.removeEdges(transitionIds));

    // Persist to Firebase (full replace for each affected screen)
    await this.wrapWithLoading(`Deleting ${transitionIds.length} transition(s)`, async () => {
      await Promise.all(
        Array.from(stepUpdates.entries()).map(([entityId, step]) => {
          const firebaseStep = v2ScreenAdapter.toFirebaseStep(step);
          return this.screenService!.replaceScreen(
            this.projectId,
            this.onboardingId,
            this.versionId,
            entityId,
            firebaseStep
          );
        })
      );
    });
  }

  getAllTransitions(): EntityTransition[] {
    return this.computeAllTransitions();
  }

  getTransition(transitionId: string): EntityTransition | undefined {
    return this.computeAllTransitions().find(t => t.id === transitionId);
  }

  // ============================================================================
  // ACCESSOR METHODS
  // ============================================================================

  getCanvasHandle(): ReactFlowCanvasHandle | null {
    return this.canvasHandle;
  }

  getEntity(id: string): FlowStepNode | undefined {
    return v2ScreenRegistry.get(id);
  }

  getScreen(screenId: string): FlowStepNode | undefined {
    return v2ScreenRegistry.get(screenId);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  destroy(): void {
    if (!this.screenService) {
      console.warn('[V2Coordinator] Already destroyed');
      return;
    }

    console.log('[V2Coordinator] Destroying...');
    v2ScreenRegistry.clear();
    this.canvasHandle = null;
    this.screenService = null;
    this.onboardingService = null;
    this.onLaunchScreenChanged = undefined;
    this.initialized = false;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private loadExistingScreensToCanvas(): void {
    if (!this.canvasHandle) {
      console.warn('[V2Coordinator] Cannot load screens - no Canvas handle');
      return;
    }

    console.log('[V2Coordinator] Loading screens to canvas...');

    const stepIds = v2ScreenRegistry.getAllIds();
    console.log(`[V2Coordinator] Loading ${stepIds.length} screens to canvas`);

    stepIds.forEach((stepId) => {
      const step = v2ScreenRegistry.get(stepId);
      if (!step) return;

      const handles = buildV2ActionHandles(step);

      const nodeData = {
        entityId: step.entityId,
        entityType: 'screen' as const,
        nodeType: 'onboarding-screen-v2' as const,
        position: step.position || { x: 0, y: 0 },
        handles,
        isLaunchScreen: step.entityId === this.launchScreenId,
      };

      this.canvasHandle!.addNode(nodeData);
    });

    // Sync all edges
    const allEdges = this.computeAllTransitions();
    this.canvasHandle!.syncEdges(allEdges);

    console.log('[V2Coordinator] Canvas load complete');
  }

  private addStepToCanvas(step: FlowStepNode): void {
    v2ScreenRegistry.register(step);

    if (this.canvasHandle) {
      const handles = buildV2ActionHandles(step);

      this.canvasHandle.addNode({
        entityId: step.entityId,
        entityType: 'screen',
        nodeType: 'onboarding-screen-v2',
        position: step.position || { x: 0, y: 0 },
        handles,
        isLaunchScreen: step.entityId === this.launchScreenId,
      });
    }
  }

  private syncEdgesToCanvas(edges?: EntityTransition[]): void {
    if (this.canvasHandle) {
      const edgesToSync = edges ?? this.computeAllTransitions();
      this.canvasHandle.syncEdges(edgesToSync);
    }
  }

  private computeTransitionsFromSteps(steps: FlowStepNode[]): EntityTransition[] {
    const transitions: EntityTransition[] = [];
    steps.forEach(step => {
      const stepTransitions = v2TransitionManager.extractTransitions(step as StepNode);
      transitions.push(...stepTransitions);
    });
    return transitions;
  }

  private computeAllTransitions(): EntityTransition[] {
    return this.computeTransitionsFromSteps(v2ScreenRegistry.getAll());
  }

  private executeCanvasOperation<T>(fn: (handle: ReactFlowCanvasHandle) => T): T | void {
    if (!this.canvasHandle) {
      return;
    }
    return fn(this.canvasHandle);
  }

  private findTransitionOwner(transitionId: string): { step: FlowStepNode; transition: EntityTransition } | undefined {
    const allSteps = v2ScreenRegistry.getAll();

    for (const step of allSteps) {
      const transitions = v2TransitionManager.extractTransitions(step as StepNode);
      const transition = transitions.find(t => t.id === transitionId);
      if (transition) {
        return { step, transition };
      }
    }

    return undefined;
  }

  /**
   * Cleanup incoming connections when screens are deleted.
   *
   * Uses V2TransitionManager.removeTransition() in a loop — much cleaner
   * than V1's manual per-action-key struct manipulation. Handles all 12
   * V2 edge locations automatically.
   */
  private async cleanupIncomingConnections(deletedStepIds: string[]): Promise<Map<string, FlowStepNode>> {
    const affectedStepsBackup = new Map<string, FlowStepNode>();
    const allTransitions = this.computeAllTransitions();

    // Find transitions where target is being deleted but source is NOT
    const incomingTransitions = allTransitions.filter(t =>
      deletedStepIds.includes(t.targetEntityId) && !deletedStepIds.includes(t.sourceEntityId)
    );

    // Group by source screen
    const transitionsBySource = new Map<string, string[]>();
    for (const transition of incomingTransitions) {
      if (!transitionsBySource.has(transition.sourceEntityId)) {
        transitionsBySource.set(transition.sourceEntityId, []);
      }
      transitionsBySource.get(transition.sourceEntityId)!.push(transition.id);
    }

    // For each affected source screen, backup and remove transitions
    for (const [sourceStepId, transitionIds] of transitionsBySource) {
      const step = v2ScreenRegistry.get(sourceStepId);
      if (!step) continue;

      // Backup before modification
      affectedStepsBackup.set(sourceStepId, JSON.parse(JSON.stringify(step)));

      // Remove each incoming transition using V2TransitionManager
      let currentStep = step as StepNode;
      for (const transitionId of transitionIds) {
        currentStep = v2TransitionManager.removeTransition(currentStep, transitionId);
      }

      // Update registry
      v2ScreenRegistry.update(sourceStepId, { ...currentStep, entityId: step.entityId } as FlowStepNode);

      // Persist to Firebase (full replace)
      if (this.screenService) {
        const firebaseStep = v2ScreenAdapter.toFirebaseStep(
          v2ScreenRegistry.get(sourceStepId)!
        );
        await this.screenService.replaceScreen(
          this.projectId,
          this.onboardingId,
          this.versionId,
          sourceStepId,
          firebaseStep
        );
      }
    }

    return affectedStepsBackup;
  }

  private createEdgeData(transition: EntityTransition): EdgeData {
    return {
      id: transition.id,
      source: transition.sourceEntityId,
      target: transition.targetEntityId,
      sourceHandle: TransitionIdGenerator.generateHandleId(
        transition.sourceEntityId,
        transition.sourceActionKey
      ),
      label: transition.label ?? '',
      type: 'transition',
      animated: false,
      data: {
        sourceEntityId: transition.sourceEntityId,
        targetEntityId: transition.targetEntityId,
        sourceActionKey: transition.sourceActionKey,
        rules: transition.rules || [],
        // Per-edge visual transition override (Route.transition) — drives the
        // canvas override marker and the TransitionEditor initial value
        transition: transition.transition,
      }
    };
  }

  // ============================================================================
  // DEBUG
  // ============================================================================

  debug(): void {
    console.log('[V2Coordinator] Debug:');
    console.log(`  projectId: ${this.projectId}`);
    console.log(`  onboardingId: ${this.onboardingId}`);
    console.log(`  versionId: ${this.versionId}`);
    console.log(`  launchScreenId: ${this.launchScreenId}`);
    const transitions = this.computeAllTransitions();
    console.log(`  Transitions: ${transitions.length}`);
    transitions.forEach((t) => {
      console.log(`    ${t.id}: ${t.sourceEntityId} → ${t.targetEntityId}`);
    });
    v2ScreenRegistry.debug();
  }
}
